import React from "react"

// Import the shadcn components (adjust the import paths to match your setup)
import { Button } from "@comps/ui/button"
import { Input } from "@comps/ui/input"
import { WorkspaceFileForImport } from "@dto/workspaces"
import { readBase64File } from "@utils/files"
import { getWsButtonStyle, useHover } from "@utils/twClasses"
import { CloseCircle, MessageQuestion } from "iconsax-react"
import { useTranslation } from "react-i18next"
import { translate, translateHtml } from "src/i18n/i18n"
import { useModelStore } from "src/store"
import CompozzSpinner from "./CompozzSpinner"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip"

// Selection of one or several Compozz files (*.cpz). Workspaces which depend on each other cannot be
// imported one after the other: they are selected and imported together.
// The selection is owned by the parent, so that it survives a refused import: the user comes back to
// it to add the file of a missing workspace.
type CompozzFileUploaderProps = {
    files: File[]
    onFilesChange: (files: File[]) => void
    onImport: (files: WorkspaceFileForImport[]) => void
    loading: boolean
    onError: (error: string) => void
}

function isSameFile(a: File, b: File): boolean {
    return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

export default function CompozzFileUploader({ files, onFilesChange, onImport, loading, onError }: CompozzFileUploaderProps) {
    const fileInputRef = React.useRef<HTMLInputElement | null>(null)
    const { t } = useTranslation();
    const wsColor = useModelStore((state) => state.wsColor);
    const wsDarkColor = useModelStore((state) => state.wsDarkColor);
    const wsLightColor = useModelStore((state) => state.wsLightColor);
    const validateHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const chooseHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    function handleUploadClick() {
        fileInputRef.current?.click()
    }

    // The chosen files are added to the selection (a file chosen twice is kept once)
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // The FileList is live: copy it before the input is emptied
        const chosen = Array.from(event.target.files || []);
        if (chosen.length > 0) {
            const added = chosen.filter(file => !files.some(selected => isSameFile(selected, file)));
            onFilesChange([...files, ...added]);
        }
        // Lets the same file be chosen again once it has been removed
        event.target.value = "";
    }

    function removeFile(file: File) {
        onFilesChange(files.filter(selected => selected !== file));
    }

    const handleUpload = () => {
        if (files.length === 0) return

        Promise.all(files.map(file => readBase64File(file))).then(contents => {
            onImport(contents.map((content, index) => ({ name: files[index].name, content: content })))
        }).catch(error => {
            onError(error)
        })
    }

    const scrollStyle = {
        scrollbarColor: wsColor + " " + wsLightColor,
    }

    return (
        <div className="flex flex-col p-4 max-w-sm mx-auto w-3/4 gap-2">
            {loading && <div className="flex flex-col justify-center items-center h-full gap-4">
                <p className="text-sm">
                    {t("compozz.file.import.loading")}
                </p>
                <CompozzSpinner />
            </div>}
            {!loading &&
                <>
                    <Input
                        ref={fileInputRef}
                        id="cpzFile"
                        type="file"
                        accept=".cpz"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    <div className="flex flex-row relative w-full">
                        <Button onClick={handleUploadClick} {...chooseHover} className="h-16 min-h-16 w-full">
                            {(files.length === 0) ? t("compozz.file.import.choose.files") : t("compozz.file.import.add.files")}
                        </Button>
                        <TooltipProvider>
                            <Tooltip delayDuration={100}>
                                <TooltipTrigger>
                                    <MessageQuestion className="absolute -right-8 top-0 h-6 w-6 min-h-6 min-w-6 text-gray-600" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    {translateHtml(t, "compozz.file.import.question.tooltip", [])}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>

                    {files.length === 0 &&
                        <p className="text-sm">{t("compozz.file.import.no.file.selected")}</p>
                    }
                    {files.length > 0 &&
                        <>
                            <p className="text-sm">{t("compozz.file.import.selected.files")} :</p>
                            <ul className="flex flex-col gap-1 max-h-40 overflow-y-auto text-sm scrollbar-thin scrollbar-button-hidden pr-2" style={scrollStyle}>
                                {files.map(file => (
                                    <li key={file.name + "-" + file.size + "-" + file.lastModified} className="flex flex-row items-center justify-between gap-2">
                                        <span className="truncate">{file.name}</span>
                                        <button type="button" aria-label={translate(t, "compozz.file.import.remove.file.1", [file.name])} onClick={() => removeFile(file)}>
                                            <CloseCircle className="h-4 w-4 min-h-4 min-w-4 text-gray-600" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    }

                    <Button onClick={handleUpload} disabled={files.length === 0} {...validateHover} className={"mt-8 h-8 min-h-8 " + (files.length > 0 ? "" : "opacity-50")}>
                        {t("compozz.file.import.validate")}
                    </Button>
                </>
            }

        </div>
    )
}
